/**
 * Direct Mail types (pin → measure → mail).
 *
 * Derived from backend/apexos/apps/marketing/models_direct_mail.py and the
 * endpoints under /api/marketing/direct-mail/. Keep these in sync with the
 * backend — do not invent fields.
 *
 * Decimal fields arrive from DRF as strings (COERCE_DECIMAL_TO_STRING is left
 * at its default), so money and coordinates are typed as strings here rather
 * than pretending they are numbers. Endpoints that return an ad-hoc dict
 * instead of a serializer render Decimals as JSON numbers, which is why the
 * aggregate stats shapes accept `number | string`. Use `moneyToNumber` /
 * `formatMoney` from lib/directMailApi.ts instead of parsing inline.
 */

/** Wire form of a DRF DecimalField. */
export type MoneyString = string;

/** Every Direct Mail list endpoint is paginated the same way. */
export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ---------------------------------------------------------------------------
// Enumerations — values must match the backend model choices exactly
// ---------------------------------------------------------------------------

/** MailerPin.STATUS_CHOICES */
export type MailerPinStatus =
  | "pinned"
  | "queued"
  | "measuring"
  | "measured"
  | "approved"
  | "batched"
  | "mailed"
  | "responded"
  | "skipped";

/** MailerPin.QUEUE_STATUSES — a pin still waiting on a human to measure it. */
export const MAILER_PIN_QUEUE_STATUSES: MailerPinStatus[] = [
  "queued",
  "measuring",
];

export const MAILER_PIN_STATUS_LABELS: Record<MailerPinStatus, string> = {
  pinned: "Pinned",
  queued: "Queued for measurement",
  measuring: "Being measured",
  measured: "Measured",
  approved: "Approved to mail",
  batched: "In a batch",
  mailed: "Mailed",
  responded: "Responded",
  skipped: "Skipped",
};

/** MailerPin.SOURCE_CHOICES */
export type MailerPinSource = "mobile" | "web" | "import";

/** MailerPin.FULFILLMENT_CHOICES — who does the measuring. */
export type MailFulfillment = "self" | "apexos";

export const MAIL_FULFILLMENT_LABELS: Record<MailFulfillment, string> = {
  self: "Our team measures",
  apexos: "ApexOS measures",
};

/** MailCampaign.TEMPLATE_CHOICES */
export type MailTemplateKind = "packet" | "postcard";

export const MAIL_TEMPLATE_LABELS: Record<MailTemplateKind, string> = {
  packet: "Estimate packet",
  postcard: "Postcard",
};

/** MailCampaign.STATUS_CHOICES */
export type MailCampaignStatus = "draft" | "active" | "archived";

/** MailBatch.STATUS_CHOICES */
export type MailBatchStatus =
  | "draft"
  | "submitted"
  | "printing"
  | "mailed"
  | "failed"
  | "cancelled";

export const MAIL_BATCH_STATUS_LABELS: Record<MailBatchStatus, string> = {
  draft: "Draft",
  submitted: "Submitted",
  printing: "Printing",
  mailed: "Mailed",
  failed: "Failed",
  cancelled: "Cancelled",
};

/** MailMeasurementOrder.STATUS_CHOICES */
export type MailMeasurementOrderStatus =
  | "pending"
  | "in_process"
  | "complete"
  | "cancelled";

export const MAIL_MEASUREMENT_ORDER_STATUS_LABELS: Record<
  MailMeasurementOrderStatus,
  string
> = {
  pending: "Pending",
  in_process: "In Process",
  complete: "Complete",
  cancelled: "Cancelled",
};

/** MailProductRate.KIND_CHOICES */
export type MailProductRateKind = "mail_piece" | "service";

/** Catalog keys seeded by migration 0043_seed_direct_mail_rates. */
export type MailProductRateKey =
  | "packet_page"
  | "packet_5page"
  | "postcard_6x9"
  | "measurement_service";

/** PITCH_BUCKET_CHOICES from models_roof_quote.py — '' before a pin is measured. */
export type PitchBucket = "low" | "med" | "steep";

/** Why an address was suppressed by check-suppression. */
export type SuppressionReason =
  | "do_not_knock"
  | "duplicate_pin"
  | "existing_customer";

export const SUPPRESSION_REASON_LABELS: Record<SuppressionReason, string> = {
  do_not_knock: "On the do-not-knock list",
  duplicate_pin: "Already pinned for this campaign",
  existing_customer: "Already a customer",
};

// ---------------------------------------------------------------------------
// MailProductRate
// ---------------------------------------------------------------------------

/**
 * A catalog price row. `vendor_cost` is deliberately absent — it is internal
 * margin data and the org-facing serializer must never include it.
 */
export interface MailProductRate {
  id: number;
  key: MailProductRateKey | string;
  kind: MailProductRateKind;
  name: string;
  description: string;
  price: MoneyString;
  sort_order: number;
  is_active: boolean;
}

/** Superuser-only view of the same row, with our cost exposed. */
export interface MailProductRateAdmin extends MailProductRate {
  vendor_cost: MoneyString;
}

export interface MailProductRateUpdatePayload {
  name?: string;
  description?: string;
  price?: MoneyString | number;
  vendor_cost?: MoneyString | number;
  sort_order?: number;
  is_active?: boolean;
}

// ---------------------------------------------------------------------------
// MailCampaign
// ---------------------------------------------------------------------------

export interface MailCampaign {
  service_area?: string;
  layout_preset?: "classic" | "bold" | "editorial";
  id: number;
  name: string;
  status: MailCampaignStatus;
  template_kind: MailTemplateKind;
  quote_tier: number | null;
  quote_tier_name?: string | null;
  qr_code: number | null;
  qr_code_name?: string | null;
  contact_source: number | null;
  contact_source_name?: string | null;
  headline: string;
  offer_text: string;
  disclaimer_text: string;
  show_financing: boolean;
  financing_months: number;
  financing_apr: MoneyString;
  created_by?: number | null;
  created_by_name?: string | null;
  pin_count?: number;
  created_at: string;
  updated_at: string;
}

export interface MailCampaignPayload {
  service_area?: string;
  layout_preset?: "classic" | "bold" | "editorial";
  name: string;
  status?: MailCampaignStatus;
  template_kind?: MailTemplateKind;
  quote_tier?: number | null;
  qr_code?: number | null;
  contact_source?: number | null;
  headline?: string;
  offer_text?: string;
  disclaimer_text?: string;
  show_financing?: boolean;
  financing_months?: number;
  financing_apr?: MoneyString | number;
}

/**
 * GET direct-mail/campaigns/{id}/stats/.
 *
 * The index signature is an escape hatch: this endpoint returns an ad-hoc dict
 * rather than a serializer, so the backend may add counters without a frontend
 * change breaking the build.
 */
export interface MailCampaignStats {
  campaign: number;
  name?: string;
  total_pins: number;
  pins_by_status: Partial<Record<MailerPinStatus, number>>;
  scans: number;
  leads: number;
  [extra: string]: unknown;
}

// ---------------------------------------------------------------------------
// MailerPin
// ---------------------------------------------------------------------------

export interface MailerPin {
  id: number;
  campaign: number | null;
  campaign_name?: string | null;
  /** UUID printed into the QR link; the /m/<token> landing page key. */
  public_token: string;
  status: MailerPinStatus;
  status_display?: string;
  source: MailerPinSource;

  formatted_address: string;
  normalized_address: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  place_id: string;
  latitude: string;
  longitude: string;
  owner_name: string;

  knock: number | null;
  measurement: number | null;
  contact: number | null;
  contact_name?: string | null;
  pinned_by: number | null;
  pinned_by_name?: string | null;

  fulfillment: MailFulfillment;
  assigned_to: number | null;
  assigned_to_name?: string | null;
  claimed_at: string | null;
  measured_at: string | null;
  measured_by: number | null;
  measured_by_name?: string | null;
  measurement_order: number | null;

  roof_area_sqft: MoneyString | null;
  pitch_bucket: PitchBucket | "";
  predominant_pitch: string;
  waste_factor: string | null;
  quote_price_low: MoneyString | null;
  quote_price_high: MoneyString | null;
  pricing_breakdown: Record<string, unknown>;

  batch: number | null;
  batch_reference?: string | null;
  provider_piece_id: string;
  mailed_at: string | null;
  delivered_at: string | null;
  piece_error: string;

  notes: string;
  skip_reason: string;
  is_measured: boolean;

  created_at: string;
  updated_at: string;
}

/** What a client sends when dropping a pin. Coordinates may be sent as numbers. */
export interface MailerPinInput {
  formatted_address: string;
  latitude: number | string;
  longitude: number | string;
  campaign?: number | null;
  source?: MailerPinSource;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  place_id?: string;
  owner_name?: string;
  knock?: number | null;
  fulfillment?: MailFulfillment;
  notes?: string;
}

export interface MailerPinUpdatePayload {
  campaign?: number | null;
  status?: MailerPinStatus;
  formatted_address?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  owner_name?: string;
  fulfillment?: MailFulfillment;
  assigned_to?: number | null;
  notes?: string;
}

export interface MailerPinListParams {
  status?: MailerPinStatus | MailerPinStatus[];
  campaign?: number;
  assigned_to?: number;
  fulfillment?: MailFulfillment;
  search?: string;
  /** Queue statuses with nobody assigned. */
  unclaimed?: boolean;
  /** Assigned to the requesting user. */
  mine?: boolean;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export interface MailerPinBulkCreateRequest {
  pins: MailerPinInput[];
  campaign?: number | null;
}

export interface MailerPinBulkCreateSkip {
  address: string;
  reason: string;
}

export interface MailerPinBulkCreateResponse {
  created: MailerPin[];
  skipped: MailerPinBulkCreateSkip[];
}

/**
 * One row of direct-mail/pins/map-data/ — deliberately tiny so a map can hold
 * thousands. Coordinates are `number | string` because this endpoint may be
 * built from a values() query (JSON numbers) or a serializer (strings); call
 * `pinPointLatLng` rather than assuming either.
 */
export interface MailerPinMapPoint {
  id: number;
  lat: number | string;
  lng: number | string;
  status: MailerPinStatus;
  formatted_address: string;
  campaign: number | null;
  quote_price_low: MoneyString | number | null;
}

export interface MailerPinMapDataResponse {
  pins: MailerPinMapPoint[];
  count: number;
}

export interface MailerPinMapDataParams {
  /** "minLng,minLat,maxLng,maxLat" */
  bbox?: string;
  campaign?: number;
  status?: MailerPinStatus | MailerPinStatus[];
}

export interface SuppressionCandidate {
  formatted_address: string;
  latitude?: number | string;
  longitude?: number | string;
}

export interface SuppressionResult {
  normalized_address: string;
  suppressed: boolean;
  reasons: SuppressionReason[];
}

export interface CheckSuppressionRequest {
  candidates: SuppressionCandidate[];
  /** Duplicate detection is per campaign, so an answer without one is about a different set of pins. */
  campaign?: number | null;
}

export interface CheckSuppressionResponse {
  results: SuppressionResult[];
}

export interface CompleteMeasurementRequest {
  roof_area_sqft: number | string;
  measurement_id?: number | null;
  predominant_pitch?: string;
  pitch_bucket?: PitchBucket;
  waste_factor?: number | string;
  tier_id?: number | null;
}

export interface SkipPinRequest {
  reason: string;
}

export interface MailerPinBulkQueueRequest {
  pin_ids: number[];
  fulfillment: MailFulfillment;
  campaign?: number | null;
}

/**
 * Response of bulk-queue. Every counter is optional because the endpoint
 * returns an ad-hoc dict; read `queued ?? updated ?? 0`.
 */
export interface MailerPinBulkQueueResponse {
  queued?: number;
  updated?: number;
  skipped?: MailerPinBulkCreateSkip[];
  pins?: MailerPin[];
  [extra: string]: unknown;
}

export interface MailerPinBulkApproveRequest {
  pin_ids: number[];
}

export interface MailerPinBulkApproveRejection {
  id: number;
  reason: string;
}

export interface MailerPinBulkApproveResponse {
  approved: number;
  rejected: MailerPinBulkApproveRejection[];
}

/** GET direct-mail/pins/queue-stats/ */
export interface MailerQueueStats {
  unclaimed: number;
  mine: number;
  measuring: number;
  measured: number;
  approved: number;
  apexos_pending: number;
}

// ---------------------------------------------------------------------------
// Orders and batches (money)
// ---------------------------------------------------------------------------

export interface MailMeasurementOrder {
  id: number;
  reference: string;
  status: MailMeasurementOrderStatus;
  pin_count: number;
  unit_price: MoneyString;
  total_amount: MoneyString;
  paid_at: string | null;
  completed_at: string | null;
  created_by?: number | null;
  created_by_name?: string | null;
  created_at: string;
  updated_at: string;
  /** Superuser fulfillment queue only. */
  organization?: number;
  organization_name?: string;
  admin_notes?: string;
}

export interface MailMeasurementOrderCreateRequest {
  pin_ids: number[];
}

export interface MailMeasurementOrderAdminUpdatePayload {
  status?: MailMeasurementOrderStatus;
  admin_notes?: string;
}

export interface MailBatch {
  proof_version?: string;
  proof_approved_at?: string | null;
  proof_approved_by?: number | null;
  id: number;
  reference: string;
  campaign: number;
  campaign_name?: string | null;
  status: MailBatchStatus;
  piece_count: number;
  unit_price: MoneyString;
  total_amount: MoneyString;
  paid_at: string | null;
  provider: string;
  provider_batch_id: string;
  proof_pdf: string | null;
  submitted_at: string | null;
  mailed_at: string | null;
  expected_delivery_date: string | null;
  error_message: string;
  created_by?: number | null;
  created_by_name?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * POST direct-mail/batches/ — the batch, plus the pins it would not take.
 *
 * The server drops pins that are on the do-not-knock list, have become
 * customers, or were claimed by another batch first, and says so per pin. It
 * was previously typed as a bare `MailBatch`, so those came back on every
 * create and nothing read them: a user selecting 200 houses and getting 170
 * pieces was told the count and never the reason.
 */
export interface MailBatchCreateResponse extends MailBatch {
  rejected?: MailerPinBulkApproveRejection[];
}

export interface MailBatchCreateRequest {
  campaign: number;
  pin_ids: number[];
}

export interface MailBatchListParams {
  campaign?: number;
  status?: MailBatchStatus;
  page?: number;
  page_size?: number;
}

/** POST direct-mail/batches/{id}/render-proof/ */
export interface RenderProofResponse {
  status: string;
}

// ---------------------------------------------------------------------------
// Events and scans
// ---------------------------------------------------------------------------

export interface MailPieceEvent {
  id: number;
  pin: number;
  event_type: string;
  occurred_at: string;
  raw: Record<string, unknown>;
  created_at: string;
}

export interface MailerScan {
  id: number;
  pin: number;
  campaign: number;
  scanned_at: string;
  ip_address: string | null;
  user_agent: string;
  converted: boolean;
  contact: number | null;
  created_at?: string;
  updated_at?: string;
}

// ---------------------------------------------------------------------------
// ROI reporting
// ---------------------------------------------------------------------------

/** One campaign's row inside the funnel report. */
export interface DirectMailCampaignStatsRow {
  campaign: number;
  name: string;
  pinned: number;
  measured: number;
  mailed: number;
  scans: number;
  leads: number;
  jobs: number;
  spend: MoneyString | number;
  cost_per_lead: MoneyString | number | null;
  [extra: string]: unknown;
}

/** GET direct-mail/stats/ */
export interface DirectMailStats {
  pinned: number;
  measured: number;
  mailed: number;
  scans: number;
  leads: number;
  jobs: number;
  spend: MoneyString | number;
  cost_per_lead: MoneyString | number | null;
  by_campaign: DirectMailCampaignStatsRow[];
  [extra: string]: unknown;
}

// ---------------------------------------------------------------------------
// Public landing page (no auth)
// ---------------------------------------------------------------------------

export interface PublicMailerOrganization {
  name: string;
  logo_url: string | null;
  phone: string;
  brand_color: string;
}

export interface PublicMailerCampaign {
  headline: string;
  offer_text: string;
  disclaimer_text: string;
  show_financing: boolean;
  financing_months?: number;
  financing_apr?: MoneyString | number;
}

export interface PublicMailerQuoteDetail {
  address: string;
  roof_area_sqft: MoneyString | number | null;
  squares: number | string | null;
  predominant_pitch: string;
  price_low: MoneyString | number | null;
  price_high: MoneyString | number | null;
  monthly_payment?: MoneyString | number | null;
}

/** GET direct-mail/public/pin/<public_token>/ — never contains internal ids. */
export interface PublicMailerQuote {
  organization: PublicMailerOrganization;
  campaign: PublicMailerCampaign;
  quote: PublicMailerQuoteDetail;
  already_claimed: boolean;
}

export interface PublicMailerClaimRequest {
  first_name: string;
  email: string;
  last_name?: string;
  phone?: string;
  message?: string;
}

export interface PublicMailerClaimResponse {
  ok: boolean;
  message?: string;
}

// ---------------------------------------------------------------------------
// Superuser fulfillment queue
// ---------------------------------------------------------------------------

export interface AdminMailerPin extends MailerPin {
  organization?: number;
  organization_name?: string;
}

export interface AdminMailerPinListParams {
  organization?: number;
  status?: MailerPinStatus;
  search?: string;
  page?: number;
  page_size?: number;
}

export interface AdminMeasurementOrderListParams {
  status?: MailMeasurementOrderStatus;
  organization?: number;
  page?: number;
  page_size?: number;
}
